'use client';

import { useState } from 'react';
import { Drawer } from 'ui/components/Drawer/drawer';
import { Button } from 'ui/components/ButtonV2/Button';
import { StaticIcon } from 'ui/components/Icon/Icon';
import { Profile } from 'ui/icons/icons/Navigation/Profile';
import { SignInForm } from 'ui/components/SignInForm/SignInForm';
import { Loading } from 'ui/components/Loading/Loading';
import { cltw } from 'ui/utils/';
import type { UserDetails } from 'ui/types/Users';
import { env } from 'env';
import { useTranslation } from '@whoop/i18n/lang/client';
import { createClientAuth } from '@whoop/auth-client';
import type { UserData } from 'services/generated/auth-service/models/UserData';
import { useShallow } from 'zustand/react/shallow';
import { getUserStrapGeneration } from '@/client/strapClient';
import { useUserStore } from '@/stores/StoreProvider';

interface ISigninDrawer {
  signInLabel: string;
  user: UserData | null;
}

const auth = createClientAuth({
  env: env.NEXT_PUBLIC_ENV,
  segmentKey: env.NEXT_PUBLIC_SEGMENT_API_KEY ?? '',
});

export function SigninDrawer({ signInLabel }: ISigninDrawer) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { setAuthToken, setStrap, setUser, user } = useUserStore(
    useShallow((state) => ({
      setAuthToken: state.setAuthToken,
      setStrap: state.setStrap,
      setUser: state.setUser,
      user: state.user,
    })),
  );

  const { t } = useTranslation('signInForm');

  const handleSuccessfulSignin = ({ user }: { user: UserData }) => {
    setIsSidebarOpen(false);
    setUser(user);
  };

  const handleSubmit = async ({
    email,
    password,
  }: UserDetails): Promise<void> => {
    auth.trackSignInPageView();
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }
      setHasError(false);
      setIsLoading(true);
      await auth.signIn({ username: email, password: password || '' });
      setAuthToken(auth.getToken());
      const userData = await auth.getUser();
      const userStrap = await getUserStrapGeneration({
        token: auth.getToken(),
      });
      setStrap(userStrap);
      if (userData) {
        handleSuccessfulSignin({ user: userData });
      }
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoutButtonClick = async () => {
    setIsSidebarOpen(false);
    setAuthToken(null);
    setUser(null);
    setStrap(null);
    await auth.signOut();
  };

  return (
    <>
      <Button
        aria-label='sign in'
        className='flex w-auto gap-2 whitespace-nowrap px-3 text-white hover:bg-black xl:px-5'
        onClick={() => setIsSidebarOpen((isOpen) => !isOpen)}
        size='medium'
        variant='transparent'
      >
        {user?.first_name && user?.last_name ? (
          <img
            alt={user.first_name}
            className='h-8 w-8 rounded-full'
            src={`https://ui-avatars.com/api/?name=${`${user.first_name} ${user.last_name}`}`}
          />
        ) : (
          <StaticIcon
            IconComponent={Profile}
            alt='sign in'
            color='#ffffff'
            size='small'
          />
        )}
        <span className='hidden xl:inline'>
          {user?.first_name ? user.first_name : signInLabel}
        </span>
      </Button>
      <Drawer
        className='bg-white text-black sm:w-[400px]'
        drawerId='sidebar'
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      >
        {auth.isAuthenticated() ? (
          <Button onClick={handleLogoutButtonClick}>Logout</Button>
        ) : (
          <div className={cltw(isLoading && 'select-none opacity-25')}>
            {isLoading ? <Loading size='large' /> : null}
            <h2 className='py-5 text-center text-sm font-bold uppercase tracking-[2px]'>
              {t('signIn')}
            </h2>
            <SignInForm loading={isLoading} onSubmit={handleSubmit} />
            <h2 className='mt-5 text-center text-sm font-semibold uppercase'>
              {t('noAccount')}{' '}
              <a
                className='font-bold text-black underline'
                href='https://join.whoop.com'
                rel='noopener noreferrer'
                target='_blank'
              >
                {t('joinNow')}
              </a>
            </h2>
            {hasError ? (
              <div className='pt-5 text-center font-semibold text-red-500'>
                {t('signInError')}
              </div>
            ) : null}
          </div>
        )}
      </Drawer>
    </>
  );
}
